// Global
import { pushToDataLayer, DataLayerEvent } from 'components/helpers/Global/Analytics/DataLayer';
import React, { ButtonHTMLAttributes, forwardRef } from 'react';

// Local
import {
  CtaContentResultAnalyticsConfig,
  pushCtaContentResultEvent,
} from 'components/helpers/Analytics/Cta/CtaAnalytics';
import { IconTypes, SvgIcon, SvgIconSize } from 'components/helpers/SvgIcon';
import { GtmEvent } from 'lib/utils/gtm-utils';

import { CtaComponentClass } from 'lib/hooks/theming/useCtaComponentClass';
import { useCtaClass } from './useCtaClass';

export type CtaVariant = 'brand' | 'inverse';
export type CtaStyle = 'filled' | 'outline' | 'link' | 'link-invert-underline' | 'none';
export type CtaSize = 'base' | 'small';

type CtaPropsNone = {
  ctaStyle: 'none';
  ctaComponentClass?: undefined;
  ctaVariant?: undefined;
  ctaSize?: CtaSize;
  ctaIconSize?: SvgIconSize;
  ctaIconBefore?: IconTypes;
  ctaIconAfter?: IconTypes;
  ctaIconNoSpacer?: boolean;
  ctaIconBeforeViewBox?: string;
  ctaIconAfterViewBox?: string;
  ctaIconClassName?: string;
};

export type CtaPropsBase = {
  ctaStyle: CtaStyle;
  ctaSize?: CtaSize;
  ctaIconSize?: SvgIconSize;
  ctaIconBefore?: IconTypes;
  ctaIconAfter?: IconTypes;
  ctaIconNoSpacer?: boolean;
  ctaIconClassName?: string;
  ctaIconBeforeViewBox?: string;
  ctaIconAfterViewBox?: string;
};

export type CtaPropsVariant = CtaPropsBase & {
  ctaComponentClass?: undefined;
  ctaVariant: CtaVariant;
};

export type CtaPropsClass = CtaPropsBase & {
  ctaComponentClass: CtaComponentClass;
  ctaVariant?: undefined;
};

export type CtaProps = CtaPropsVariant | CtaPropsClass | CtaPropsNone;

export type CtaButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  CtaProps &
  React.PropsWithChildren & {
    className?: string;
    contentResultAnalytics?: CtaContentResultAnalyticsConfig;
    gtmEvent?: GtmEvent;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    text?: string;
  };

const CtaButton = forwardRef<HTMLButtonElement, CtaButtonProps>(
  (
    {
      className,
      ctaComponentClass,
      ctaVariant,
      ctaStyle,
      ctaSize = 'base',
      disabled,
      ctaIconSize = 'xs',
      ctaIconBefore,
      ctaIconAfter,
      ctaIconNoSpacer,
      ctaIconBeforeViewBox,
      ctaIconAfterViewBox,
      contentResultAnalytics,
      gtmEvent,
      onClick,
      text,
      children,
      ctaIconClassName,
      ...rest
    }: CtaButtonProps,
    ref
  ): JSX.Element | null => {
    /*
     * EVENT HANDLERS
     */

    const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      pushCtaContentResultEvent({
        contentListName: contentResultAnalytics?.contentListName,
      });

      if (gtmEvent) {
        pushToDataLayer({ ...gtmEvent } as DataLayerEvent);
      }

      if (onClick) {
        onClick(e);
      }
    };

    const ctaClass = useCtaClass(ctaStyle, className, ctaComponentClass, ctaVariant, ctaSize);

    /*
     * RENDERING
     */

    // If no content is present, don't print
    if (!text && !children) return <></>;

    return (
      <button {...rest} className={ctaClass} disabled={disabled} onClick={handleOnClick} ref={ref}>
        {ctaIconBefore && (
          <SvgIcon
            className={ctaIconClassName || 'cta-icon-default'}
            icon={ctaIconBefore}
            size={ctaIconSize}
            viewBox={ctaIconBeforeViewBox || '0 0 24 24'}
            iconPosition={ctaIconNoSpacer ? 'none' : 'before'}
          />
        )}
        {text}
        {children}
        {ctaIconAfter && (
          <SvgIcon
            className={ctaIconClassName || 'cta-icon-default'}
            icon={ctaIconAfter}
            size={ctaIconSize}
            viewBox={ctaIconAfterViewBox || '0 0 24 24'}
            iconPosition={ctaIconNoSpacer ? 'none' : 'after'}
          />
        )}
      </button>
    );
  }
);

CtaButton.displayName = 'CtaButton';

export default CtaButton;
